import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, X, Upload, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg } from "@/lib/api";

const LETTERS = "ABCDEF";
const EMPTY = { text: "", options: ["", "", "", ""], correct_index: 0, explanation: "" };

const CSV_TEMPLATE =
`question,optionA,optionB,optionC,optionD,correct,explanation
What is 2+2?,3,4,5,22,B,Basic arithmetic
Capital of India?,Mumbai,Delhi,Kolkata,Chennai,B,
Powerhouse of the cell?,Nucleus,Mitochondria,Ribosome,ER,2,Textbook definition
`;

const QuestionForm = ({ open, onOpenChange, initial, testId, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initial ? { ...initial, options: [...initial.options] } : { ...EMPTY, options: ["", "", "", ""] }); }, [open, initial]);
  const setOpt = (i, v) => { const o = [...f.options]; o[i] = v; setF({ ...f, options: o }); };
  const removeOpt = (i) => {
    const o = f.options.filter((_, j) => j !== i);
    setF({ ...f, options: o, correct_index: f.correct_index >= o.length ? 0 : f.correct_index > i ? f.correct_index - 1 : f.correct_index });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { text: f.text, options: f.options, correct_index: Number(f.correct_index), explanation: f.explanation };
      if (initial?.id) await api.put(`/admin/questions/${initial.id}`, body); else await api.post(`/admin/tests/${testId}/questions`, body);
      toast.success(initial?.id ? "Question updated" : "Question added");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="question-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit question" : "New question"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Question</Label><Textarea required rows={3} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="admin-question-text-input" /></div>
          <div className="space-y-2">
            <Label>Options</Label>
            {f.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold w-6 text-slate-500">{LETTERS[i]}</span>
                <Input required value={o} onChange={(e) => setOpt(i, e.target.value)} data-testid={`admin-option-input-${i}`} />
                {f.options.length > 2 && <Button type="button" size="icon" variant="ghost" onClick={() => removeOpt(i)} data-testid={`admin-remove-option-${i}`}><X className="h-4 w-4" /></Button>}
              </div>
            ))}
            {f.options.length < 6 && (
              <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, options: [...f.options, ""] })} data-testid="admin-add-option-button">
                <Plus className="h-4 w-4 mr-1" />Add option
              </Button>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Correct option</Label>
            <Select value={String(f.correct_index)} onValueChange={(v) => setF({ ...f, correct_index: Number(v) })}>
              <SelectTrigger data-testid="admin-correct-option-select"><SelectValue /></SelectTrigger>
              <SelectContent>{f.options.map((o, i) => <SelectItem key={i} value={String(i)} data-testid={`admin-correct-option-${i}`}>{LETTERS[i]}{o ? ` — ${o.slice(0, 40)}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Explanation (optional)</Label><Textarea value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} data-testid="admin-question-explanation-input" /></div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="admin-save-question-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">Save question</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BulkImportDialog = ({ open, onOpenChange, testId, onImported }) => {
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { if (open) { setCsvText(""); setFileName(""); setResult(null); } }, [open]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsText(f);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "pinslib-questions-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!csvText.trim()) { toast.error("Paste CSV or choose a file first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tests/${testId}/questions/bulk`, { csv: csvText });
      setResult(data);
      if (data.inserted > 0) toast.success(`Imported ${data.inserted} question${data.inserted > 1 ? "s" : ""}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} row(s) skipped — see details below`);
      onImported();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <div className="h-10 w-10 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] grid place-items-center mb-2">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <DialogTitle className="font-heading text-2xl">Bulk import questions</DialogTitle>
          <DialogDescription>
            Upload or paste a CSV with columns: <span className="font-mono">question, optionA, optionB, optionC, optionD, correct, explanation</span>.
            The <span className="font-mono">correct</span> column accepts A/B/C/D or 1-4. Blank option columns are dropped (minimum 2 options per row).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="bulk-import-choose-file">
              <Upload className="h-4 w-4 mr-1.5" />Choose CSV file
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="bulk-import-file-input" />
            {fileName && <span className="text-sm text-slate-500 font-mono truncate max-w-[200px]">{fileName}</span>}
            <Button type="button" variant="ghost" size="sm" onClick={downloadTemplate} data-testid="bulk-import-download-template" className="ml-auto hover:bg-slate-900 hover:text-white">
              <Download className="h-4 w-4 mr-1" />Download template
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>CSV content</Label>
            <Textarea
              rows={10}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={CSV_TEMPLATE}
              className="font-mono text-xs"
              data-testid="bulk-import-csv-textarea"
            />
          </div>

          {result && (
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-2" data-testid="bulk-import-result">
              <div className="text-sm">
                <span className="font-semibold text-green-700">{result.inserted}</span> inserted
                {" · "}
                <span className="font-semibold text-slate-600">{result.received_rows}</span> rows read
                {result.errors?.length ? <> · <span className="font-semibold text-red-700">{result.errors.length}</span> skipped</> : null}
              </div>
              {result.errors?.length > 0 && (
                <ul className="text-xs text-red-700 list-disc pl-4 max-h-32 overflow-y-auto" data-testid="bulk-import-errors">
                  {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-import-close">Close</Button>
          <Button type="button" onClick={submit} disabled={busy || !csvText.trim()} data-testid="bulk-import-submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
            {busy ? "Importing…" : "Import questions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminQuestions() {
  const { testId } = useParams();
  const [test, setTest] = useState(null);
  const [qs, setQs] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/tests/${testId}/questions`).then((r) => setQs(r.data));
  useEffect(() => {
    api.get(`/admin/tests/${testId}`).then((r) => setTest(r.data)).catch((e) => toast.error(errMsg(e)));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  const remove = async (q) => {
    try { await api.delete(`/admin/questions/${q.id}`); toast.success("Question deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <Link to="/admin/tests" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-tests-link"><ArrowLeft className="h-4 w-4" />All tests</Link>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-3">
        <div>
          <div className="small-label">{test?.subject || "Questions"}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2" data-testid="admin-questions-test-title">{test?.title || "…"}</h1>
          <p className="text-slate-500 text-sm mt-1">{qs.length} question{qs.length !== 1 && "s"} · {test?.duration_minutes} min</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="admin-bulk-import-button" className="hover:bg-slate-900 hover:text-white">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" />Import CSV
          </Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-question-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
            <Plus className="h-4 w-4 mr-1.5" />Add question
          </Button>
        </div>
      </div>
      <div className="mt-8 space-y-3">
        {qs.length === 0 && <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">No questions yet. Students won't see this test until it has at least one.</div>}
        {qs.map((q, i) => (
          <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`admin-question-card-${q.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="small-label">Q{i + 1}</div>
                <p className="font-medium text-slate-900 mt-1 whitespace-pre-wrap">{q.text}</p>
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  {q.options.map((o, j) => (
                    <div key={j} className={`text-sm border rounded-md px-3 py-1.5 ${j === q.correct_index ? "border-green-300 bg-green-50 text-green-800" : "border-slate-200 text-slate-600"}`}>
                      <span className="font-mono font-semibold mr-2">{LETTERS[j]}</span>{o}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex shrink-0">
                <Button size="icon" variant="ghost" onClick={() => { setEditing(q); setOpen(true); }} data-testid={`admin-edit-question-${q.id}`}><Pencil className="h-4 w-4" /></Button>
                <ConfirmDelete testId={`admin-delete-question-${q.id}`} title="Delete question?" description="This can't be undone." onConfirm={() => remove(q)}
                  trigger={<Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-question-${q.id}`}><Trash2 className="h-4 w-4" /></Button>} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <QuestionForm open={open} onOpenChange={setOpen} initial={editing} testId={testId} onSaved={load} />
      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} testId={testId} onImported={load} />
    </div>
  );
}
